import React, { useCallback, useEffect, useState } from 'react';
import { secureFetch } from '../../supabaseService';

function formatMoney(value) {
  return Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
}

function insufficientBalanceMessage(result) {
  const code = result?.code;
  if (code === 'CLIENT_INSUFFICIENT_BALANCE' || code === 'INSUFFICIENT_BALANCE') {
    return 'Client wallet insufficient — ask client to recharge';
  }
  return result?.message || 'Approve failed';
}

export default function ContractTerminationApprovals({ onQueueChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [feeEdits, setFeeEdits] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await secureFetch('/api/admin/terminations?status=pending');
      if (data?.success === false) throw new Error(data.message || 'Failed to load');
      const rows = Array.isArray(data?.items) ? data.items : [];
      setItems(rows);
      onQueueChange?.(rows.length);
    } catch (err) {
      setError(err?.message || 'Failed to load terminations');
      setItems([]);
      onQueueChange?.(0);
    } finally {
      setLoading(false);
    }
  }, [onQueueChange]);

  useEffect(() => {
    load();
  }, [load]);

  const handleApproveTermination = async (row) => {
    if (busyId) return;
    setBusyId(row.id);
    setError('');
    try {
      const edited = feeEdits[row.id];
      const body = {};
      if (edited != null && edited !== '' && Number.isFinite(Number(edited))) {
        body.modifiedFee = Number(edited);
      }
      const result = await secureFetch(`/api/admin/terminations/${row.id}/approve`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      if (result?.success === false) {
        throw new Error(insufficientBalanceMessage(result));
      }
      await load();
    } catch (err) {
      setError(err?.message || 'Approve failed');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      {loading && <p className="m-0 text-[12px] text-slate-400">Loading terminations…</p>}
      {error && <p className="mb-2 text-[12px] text-red-400">{error}</p>}
      {!loading && items.length === 0 && (
        <p className="m-0 text-[12px] text-slate-500">No pending contract terminations.</p>
      )}
      <div className="flex flex-col gap-3">
        {items.map((row) => {
          const ref = row.bookings?.booking_ref || row.booking_id;
          const initiator = row.initiator?.name || row.initiated_by || '—';
          const feeNet = Number(row.termination_fee_net || 0);
          const clientBalance = Number(row.client_wallet_balance ?? 0);
          return (
            <div
              key={row.id}
              className="rounded-lg border border-[#C9A84C]/25 bg-[#030810]/80 p-3 text-[12px] text-slate-300"
            >
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <span className="font-semibold text-[#C9A84C]">#{ref}</span>
                <span className="rounded-full border border-white/20 px-2 py-0.5 text-[10px] uppercase">
                  {row.status}
                </span>
              </div>
              <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
                <span>Type: {row.termination_type || '—'}</span>
                <span>
                  Fee (net): ₹{formatMoney(feeNet)}
                  {' · '}
                  Client wallet: ₹{formatMoney(clientBalance)}
                </span>
                <span>Initiated by: {initiator}</span>
                <span>
                  At: {row.initiated_at
                    ? new Date(row.initiated_at).toLocaleString('en-IN')
                    : '—'}
                </span>
              </div>
              <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-end">
                <label className="flex-1 text-[10px] text-slate-400">
                  Modify fee (gross ₹, optional)
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={feeEdits[row.id] ?? ''}
                    onChange={(e) => setFeeEdits((prev) => ({ ...prev, [row.id]: e.target.value }))}
                    placeholder={String(row.termination_fee_gross ?? '')}
                    className="mt-1 w-full rounded border border-[#C9A84C]/35 bg-[#030810] px-2 py-1.5 text-slate-100"
                  />
                </label>
                <button
                  type="button"
                  disabled={busyId === row.id}
                  onClick={() => handleApproveTermination(row)}
                  className="rounded border border-[#C9A84C]/45 bg-[#C9A84C]/15 px-3 py-2 text-[11px] font-semibold text-[#C9A84C] disabled:opacity-50"
                >
                  {busyId === row.id ? 'Approving…' : 'Approve'}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
